/**
 * Provider Billing Admin Component
 * 
 * Stripe subscriptions overview for provider admins
 * Displays all Stripe subscriptions with filtering and search
 * 
 * Features:
 * - List all Stripe subscriptions
 * - Filter by subscription status
 * - Search by tenant name
 * - View in Stripe Dashboard (opens in new tab)
 * - Copy Stripe customer/subscription ID to clipboard
 * - Create new subscriptions
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import { EnhancedTable, type TableColumn, type TableAction } from '../ui';
import { AppIconButton, AppListPage } from '../design-system';
import {
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  MoreTime as MoreTimeIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  EditNote as EditNoteIcon,
  Payments as PaymentsIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { providerStripeService } from '../../services/ProviderStripeService';
import * as dt from '../../../../shared/src/data.types';
import ProductAssignmentDialog from './ProductAssignmentDialog';
import EnterpriseLifecycleDialog, {
  type EnterpriseLifecycleMode,
} from './EnterpriseLifecycleDialog';
import SubscriptionTypeChooserDialog, {
  type SubscriptionBillingType,
} from './SubscriptionTypeChooserDialog';
import EnterprisePackageAssignmentDialog from './EnterprisePackageAssignmentDialog';

interface TenantSubscriptionRow {
  subscriptionId: string;
  billingMode: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  tenantId: string | null;
  tenantName: string;
  productName: string;
  status: string;
  dbStatus?: string;
  hasAccess?: boolean;
  accessReason?: string | null;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  trialEnd: Date | null;
  cancelAt: Date | null;
  stripeDashboardUrl: string;
  contractPoNumber?: string | null;
  invoiceDueAt?: Date | null;
}

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  [dt.SUBSCRIPTION_STATUS.Active]: 'Active',
  [dt.SUBSCRIPTION_STATUS.Trialing]: 'Trialing',
  [dt.SUBSCRIPTION_STATUS.Paused]: 'Paused',
  [dt.SUBSCRIPTION_STATUS.PastDue]: 'Past Due',
  [dt.SUBSCRIPTION_STATUS.Canceled]: 'Canceled',
  [dt.SUBSCRIPTION_STATUS.Incomplete]: 'Incomplete',
  [dt.SUBSCRIPTION_STATUS.Expired]: 'Expired',
};

const formatSubscriptionStatus = (status: string): string =>
  SUBSCRIPTION_STATUS_LABELS[status] ?? status;

const getEnterpriseDbStatus = (row: TenantSubscriptionRow): string =>
  row.dbStatus ?? row.status;

const ProviderBillingAdmin: React.FC = () => {
  // State management
  const [subscriptions, setSubscriptions] = useState<TenantSubscriptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [enterpriseAssignOpen, setEnterpriseAssignOpen] = useState(false);
  const [enterpriseAssignKind, setEnterpriseAssignKind] = useState<dt.ENTERPRISE_PACKAGE_KIND | undefined>();
  const [enterpriseDialogOpen, setEnterpriseDialogOpen] = useState(false);
  const [enterpriseDialogMode, setEnterpriseDialogMode] =
    useState<EnterpriseLifecycleMode>('extend-pilot');
  const [enterpriseTenantId, setEnterpriseTenantId] = useState<string | undefined>();
  const [enterpriseTenantName, setEnterpriseTenantName] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'warning' | 'info',
  });

  // Filtered subscriptions based on active filters
  const filteredSubscriptions = useMemo(() => {
    let result = [...subscriptions];

    // Apply status filter
    if (filters.status) {
      result = result.filter(sub => sub.status === filters.status);
    }

    if (filters.billingMode) {
      result = result.filter(sub => sub.billingMode === filters.billingMode);
    }

    return result;
  }, [subscriptions, filters]);

  // Load subscriptions data
  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const params: { limit: number } = {
        limit: 100,
      };

      const data = await providerStripeService.listStripeSubscriptions(params);
      setSubscriptions(data.subscriptions);
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to load subscriptions',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  // Handle copy to clipboard
  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: `${label} copied to clipboard`,
      severity: 'success',
    });
  };

  const openEnterpriseDialog = (
    mode: EnterpriseLifecycleMode,
    tenantId?: string | null,
    tenantName?: string,
  ) => {
    setEnterpriseDialogMode(mode);
    setEnterpriseTenantId(tenantId ?? undefined);
    setEnterpriseTenantName(tenantName ?? 'Unknown tenant');
    setEnterpriseDialogOpen(true);
  };

  const handleEnterpriseSuccess = async () => {
    await loadSubscriptions();
    setSnackbar({
      open: true,
      message: 'Enterprise subscription updated',
      severity: 'success',
    });
  };

  // Handle create subscription
  const handleCreateSubscription = () => {
    setChooserOpen(true);
  };

  const handleSubscriptionTypeSelected = (type: SubscriptionBillingType) => {
    setChooserOpen(false);
    if (type === 'stripe') {
      setCreateDialogOpen(true);
      return;
    }
    setEnterpriseAssignKind(undefined);
    setEnterpriseAssignOpen(true);
  };

  const openEnterpriseAssignDialog = (
    kind?: dt.ENTERPRISE_PACKAGE_KIND,
    tenantId?: string | null,
  ) => {
    setEnterpriseAssignKind(kind);
    setEnterpriseTenantId(tenantId ?? undefined);
    setEnterpriseAssignOpen(true);
  };

  const handleDialogSuccess = async () => {
    // Wait for subscriptions to reload before showing success message
    await loadSubscriptions();
    setSnackbar({
      open: true,
      message: 'Subscription created successfully',
      severity: 'success',
    });
  };

  // Format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get status chip color
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
    switch (status) {
      case dt.SUBSCRIPTION_STATUS.Active:
        return 'success';
      case dt.SUBSCRIPTION_STATUS.Trialing:
        return 'info';
      case dt.SUBSCRIPTION_STATUS.Paused:
        return 'warning';
      case dt.SUBSCRIPTION_STATUS.PastDue:
        return 'warning';
      case dt.SUBSCRIPTION_STATUS.Incomplete:
        return 'warning';
      case dt.SUBSCRIPTION_STATUS.Canceled:
      case dt.SUBSCRIPTION_STATUS.Expired:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusTooltip = (row: TenantSubscriptionRow): string | undefined => {
    const parts: string[] = [];
    if (row.accessReason) {
      parts.push(row.accessReason);
    }
    if (row.dbStatus && row.dbStatus !== row.status) {
      parts.push(`Database status: ${formatSubscriptionStatus(row.dbStatus)}`);
    }
    if (row.hasAccess === false && !row.accessReason) {
      parts.push('Tenant access is blocked');
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  // Handle export to CSV
  const handleExport = () => {
    const headers = ['Tenant Name', 'Status', 'Product', 'Subscription ID', 'Customer ID', 'Period End'];
    const rows = filteredSubscriptions.map(sub => [
      sub.tenantName,
      formatSubscriptionStatus(sub.status),
      sub.productName,
      sub.stripeSubscriptionId,
      sub.stripeCustomerId,
      formatDate(sub.currentPeriodEnd?.toString() ?? null),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tenant-subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: 'Subscriptions exported successfully',
      severity: 'success',
    });
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Record<string, unknown>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  // Filter options configuration
  const filterOptions = [
    {
      field: 'billingMode',
      label: 'Billing mode',
      type: 'select' as const,
      options: [
        { value: dt.BILLING_MODE.Stripe, label: 'Stripe' },
        { value: dt.BILLING_MODE.EnterpriseManual, label: 'Enterprise' },
      ],
    },
    {
      field: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: dt.SUBSCRIPTION_STATUS.Active, label: 'Active' },
        { value: dt.SUBSCRIPTION_STATUS.Trialing, label: 'Trialing' },
        { value: dt.SUBSCRIPTION_STATUS.Paused, label: 'Paused' },
        { value: dt.SUBSCRIPTION_STATUS.PastDue, label: 'Past Due' },
        { value: dt.SUBSCRIPTION_STATUS.Canceled, label: 'Canceled' },
        { value: dt.SUBSCRIPTION_STATUS.Incomplete, label: 'Incomplete' },
        { value: dt.SUBSCRIPTION_STATUS.Expired, label: 'Expired' },
      ],
    },
  ];

  // EnhancedTable columns
  const columns: TableColumn<TenantSubscriptionRow>[] = [
    {
      field: 'tenantName',
      headerName: 'Tenant Name',
      flex: 1.2,
      minWidth: 180,
      sortable: true,
    },
    {
      field: 'billingMode',
      headerName: 'Billing',
      flex: 0.7,
      minWidth: 110,
      sortable: true,
      renderCell: (row: TenantSubscriptionRow) => (
        <Chip
          label={row.billingMode === dt.BILLING_MODE.EnterpriseManual ? 'Enterprise' : 'Stripe'}
          color={row.billingMode === dt.BILLING_MODE.EnterpriseManual ? 'secondary' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 120,
      sortable: true,
      renderCell: (row: TenantSubscriptionRow) => {
        const tooltip = getStatusTooltip(row);
        const chip = (
          <Chip
            label={formatSubscriptionStatus(row.status)}
            color={getStatusColor(row.status)}
            size="small"
          />
        );
        return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
      },
    },
    {
      field: 'productName',
      headerName: 'Product',
      flex: 1.2,
      minWidth: 180,
      sortable: true,
    },
    {
      field: 'stripeSubscriptionId',
      headerName: 'Subscription ID',
      flex: 1.3,
      minWidth: 200,
      renderCell: (row: TenantSubscriptionRow) => {
        const displayId = row.billingMode === dt.BILLING_MODE.EnterpriseManual
          ? row.subscriptionId
          : row.stripeSubscriptionId;
        if (!displayId) {
          return '-';
        }
        return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {displayId.substring(0, 24)}...
          </Typography>
          <Tooltip title="Copy ID">
            <AppIconButton
              size="small"
              onClick={() => handleCopyToClipboard(displayId, 'Subscription ID')}
              sx={{ ml: 0.5 }}
            >
              <CopyIcon fontSize="small" />
            </AppIconButton>
          </Tooltip>
        </Box>
        );
      },
    },
    {
      field: 'stripeCustomerId',
      headerName: 'Stripe Customer',
      flex: 1.1,
      minWidth: 160,
      renderCell: (row: TenantSubscriptionRow) => {
        if (!row.stripeCustomerId) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {row.stripeCustomerId.substring(0, 20)}...
          </Typography>
          <Tooltip title="Copy Customer ID">
            <AppIconButton
              size="small"
              onClick={() => handleCopyToClipboard(row.stripeCustomerId, 'Customer ID')}
              sx={{ ml: 0.5 }}
            >
              <CopyIcon fontSize="small" />
            </AppIconButton>
          </Tooltip>
        </Box>
        );
      },
    },
    {
      field: 'trialEnd',
      headerName: 'Trial End',
      flex: 0.9,
      minWidth: 120,
      sortable: true,
      valueFormatter: (value) => formatDate((value as Date | null)?.toString() ?? null),
    },
    {
      field: 'currentPeriodEnd',
      headerName: 'Period End',
      flex: 0.9,
      minWidth: 130,
      sortable: true,
      valueFormatter: (value) => formatDate((value as Date | null)?.toString() ?? null),
    },
  ];

  const actions: TableAction<TenantSubscriptionRow>[] = [
    {
      label: 'View in Stripe',
      icon: <OpenInNewIcon fontSize="small" />,
      onClick: (row: TenantSubscriptionRow) => {
        window.open(row.stripeDashboardUrl, '_blank', 'noopener,noreferrer');
      },
      color: 'primary',
      visible: (row: TenantSubscriptionRow) =>
        row.billingMode !== dt.BILLING_MODE.EnterpriseManual && !!row.stripeSubscriptionId,
    },
    {
      label: 'Extend pilot',
      icon: <MoreTimeIcon fontSize="small" />,
      onClick: (row: TenantSubscriptionRow) =>
        openEnterpriseDialog('extend-pilot', row.tenantId, row.tenantName),
      visible: (row: TenantSubscriptionRow) =>
        row.billingMode === dt.BILLING_MODE.EnterpriseManual &&
        getEnterpriseDbStatus(row) === dt.SUBSCRIPTION_STATUS.Trialing,
    },
    {
      label: 'Convert to contract',
      icon: <AssignmentTurnedInIcon fontSize="small" />,
      onClick: (row: TenantSubscriptionRow) =>
        openEnterpriseAssignDialog(dt.ENTERPRISE_PACKAGE_KIND.Annual, row.tenantId),
      visible: (row: TenantSubscriptionRow) =>
        row.billingMode === dt.BILLING_MODE.EnterpriseManual &&
        (row.status === dt.SUBSCRIPTION_STATUS.Trialing ||
          row.status === dt.SUBSCRIPTION_STATUS.Expired),
    },
    {
      label: 'Apply amendment',
      icon: <EditNoteIcon fontSize="small" />,
      onClick: (row: TenantSubscriptionRow) =>
        openEnterpriseAssignDialog(dt.ENTERPRISE_PACKAGE_KIND.Amendment, row.tenantId),
      visible: (row: TenantSubscriptionRow) =>
        row.billingMode === dt.BILLING_MODE.EnterpriseManual &&
        (row.status === dt.SUBSCRIPTION_STATUS.Active ||
          row.status === dt.SUBSCRIPTION_STATUS.Trialing ||
          row.status === dt.SUBSCRIPTION_STATUS.PastDue),
    },
    {
      label: 'Record payment',
      icon: <PaymentsIcon fontSize="small" />,
      onClick: (row: TenantSubscriptionRow) =>
        openEnterpriseDialog('record-payment', row.tenantId, row.tenantName),
      visible: (row: TenantSubscriptionRow) =>
        row.billingMode === dt.BILLING_MODE.EnterpriseManual &&
        row.status !== dt.SUBSCRIPTION_STATUS.Trialing,
    },
    {
      label: 'Expire pilot',
      icon: <EventBusyIcon fontSize="small" />,
      color: 'error',
      onClick: (row: TenantSubscriptionRow) =>
        openEnterpriseDialog('expire-pilot', row.tenantId, row.tenantName),
      visible: (row: TenantSubscriptionRow) =>
        row.billingMode === dt.BILLING_MODE.EnterpriseManual &&
        getEnterpriseDbStatus(row) === dt.SUBSCRIPTION_STATUS.Trialing,
    },
  ];

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadSubscriptions()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadSubscriptions, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <AppListPage>
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{ flexShrink: 0 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      )}

      <Alert severity="info" sx={{ flexShrink: 0 }}>
        Use <strong>+ Subscription</strong> to assign a Stripe product or enterprise package.
        Enterprise lifecycle actions (extend pilot, record payment, expire) stay on each row.
        Define enterprise packages on Products &amp; Pricing → Enterprise.
      </Alert>

      <EnhancedTable<TenantSubscriptionRow>
        data={filteredSubscriptions}
        totalCount={filteredSubscriptions.length}
        loading={loading}
        columns={columns}
        actions={actions}
        getRowId={(row) => row.subscriptionId}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(size: number) => {
          setPageSize(size);
          setPage(1);
        }}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search by tenant name..."
        onSearchChange={setSearchTerm}
        onAdd={handleCreateSubscription}
        addLabel="Subscription"
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        exportable
        onExport={handleExport}
        emptyStateTitle="No subscriptions found"
        emptyStateDescription="Try adjusting your search or create a new subscription"
        rowHeight={56}
        stickyHeader
        maxHeight="100%"
      />

      <SubscriptionTypeChooserDialog
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onSelect={handleSubscriptionTypeSelected}
      />

      <ProductAssignmentDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />

      <EnterprisePackageAssignmentDialog
        open={enterpriseAssignOpen}
        onClose={() => {
          setEnterpriseAssignOpen(false);
          setEnterpriseAssignKind(undefined);
          setEnterpriseTenantId(undefined);
        }}
        onSuccess={() => void handleEnterpriseSuccess()}
        packageKind={enterpriseAssignKind}
        preselectedTenantId={enterpriseTenantId}
        title={
          enterpriseAssignKind === dt.ENTERPRISE_PACKAGE_KIND.Annual
            ? 'Convert to annual package'
            : enterpriseAssignKind === dt.ENTERPRISE_PACKAGE_KIND.Amendment
              ? 'Apply entitlement amendment'
              : 'Assign enterprise package'
        }
      />

      {enterpriseTenantId && (
        <EnterpriseLifecycleDialog
          open={enterpriseDialogOpen}
          mode={enterpriseDialogMode}
          tenantId={enterpriseTenantId}
          tenantName={enterpriseTenantName}
          onClose={() => setEnterpriseDialogOpen(false)}
          onSuccess={() => void handleEnterpriseSuccess()}
        />
      )}
    </AppListPage>
  );
};

export default ProviderBillingAdmin;
